import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

import { LanguageSwitcher } from "../components/LanguageSwitcher";

export default function NewCase() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_name: searchParams.get("client_name") || "",
    client_id: searchParams.get("client_id") || "",
    postal_code: searchParams.get("postal_code") || "",
  });

  const handleSubmit = async (e) => {
    // ... existing handleSubmit ...
    e.preventDefault();

    if (!formData.client_name.trim()) {
      toast.error(t('error_client_name_required'));
      return;
    }
    if (!formData.client_id.trim()) {
      toast.error(t('error_client_id_required'));
      return;
    }
    if (!formData.postal_code.trim()) {
      toast.error(t('error_postal_code_required'));
      return;
    }

    try {
      setLoading(true);
      const response = await api.createCase(formData);
      toast.success(t('success_case_created'));
      navigate(`/case/${response.data.id}/record`);
    } catch (error) {
      console.error("Failed to create case:", error);
      toast.error(t('error_create_case'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="back-to-dashboard">
              <Button variant="ghost" size="sm" className="rounded-sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 font-['Manrope']" data-testid="new-case-title">
                {t('new_assessment_title')}
              </h1>
              <p className="text-sm text-slate-500">{t('new_assessment_subtitle')}</p>
            </div>
            <div className="ml-auto">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-['Manrope']">{t('client_info_title')}</CardTitle>
            <CardDescription>
              {t('client_info_desc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Client Name */}
                <div>
                  <Label htmlFor="client_name" className="text-slate-700">
                    {t('client_name_label')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder={t('enter_name_placeholder')}
                    className="mt-1.5 h-11 rounded-sm border-slate-300"
                    data-testid="client-name-input"
                    required
                  />
                </div>

                {/* Client ID */}
                <div>
                  <Label htmlFor="client_id" className="text-slate-700">
                    {t('client_id_label')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client_id"
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    placeholder={t('client_id_placeholder')}
                    className="mt-1.5 h-11 rounded-sm border-slate-300 font-mono"
                    data-testid="client-id-input"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {t('client_id_help')}
                  </p>
                </div>

                {/* Postal Code */}
                <div>
                  <Label htmlFor="postal_code" className="text-slate-700">
                    {t('postal_code_label')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder={t('postal_code_placeholder')}
                    className="mt-1.5 h-11 rounded-sm border-slate-300"
                    data-testid="postal-code-input"
                    required
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <Link to="/">
                  <Button type="button" variant="ghost" className="rounded-sm">
                    {t('cancel')}
                  </Button>
                </Link>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-slate-700 hover:bg-slate-800 rounded-sm px-6"
                  data-testid="create-case-btn"
                >
                  {loading ? t('creating') : t('create_start_recording')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
